import React, { RefObject, useCallback } from "react";
import SortAscendingIcon from "../../../../../../../../../../../assets/svg/sort-ascending-icon";
import SearchIcon from "../../../../../../../../../../../assets/svg/search-icon";
import CloseIcon from "../../../../../../../../../../../assets/svg/close-icon";
import MaximizeIcon from "../../../../../../../../../../../assets/svg/maximize-icon";
import ActivitySortDropdown from "./ActivitySortDropdown";

interface ActivityMainHeaderProps {
  showControls: boolean;
  isSearchOpen: boolean;
  searchValue: string;
  enableSort: string | null;
  sortOrder: "newest" | "oldest" | null;
  sortButtonRef: RefObject<HTMLDivElement | null>;
  searchInputRef: RefObject<HTMLInputElement | null>;
  onSearchOpen: () => void;
  onSearchClose: () => void;
  onSearchChange: (value: string) => void;
  onSortToggle: () => void;
  onSortClose: () => void;
  onSort: (order: "newest" | "oldest") => void;
  onSortReset: () => void;
  onExpand?: () => void;
}

const ActivityMainHeader = ({
  showControls,
  isSearchOpen,
  searchValue,
  enableSort,
  sortOrder,
  sortButtonRef,
  searchInputRef,
  onSearchOpen,
  onSearchClose,
  onSearchChange,
  onSortToggle,
  onSortClose,
  onSort,
  onSortReset,
  onExpand,
}: ActivityMainHeaderProps) => {
  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e?.target?.value || "");
    },
    [onSearchChange]
  );

  return (
    <div className="flex items-center justify-between h-[50px] px-3 border-b border-border-gray dark:border-border-gray-dk shrink-0">
      <div className="flex items-center gap-1">
        <h3 className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
          Activity
        </h3>
      </div>

      <div className="flex items-center gap-2">
        {showControls && onExpand && (
          <button
            onClick={onExpand}
            className="p-0.5 hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded cursor-pointer"
          >
            <MaximizeIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        )}
        {showControls && (
          <div
            className="flex items-center gap-1 cursor-pointer"
            ref={sortButtonRef as React.RefObject<HTMLDivElement>}
          >
            <div
              className={`mt-1 transition-all duration-300 ease-in-out overflow-hidden ${
                isSearchOpen
                  ? "max-w-0 opacity-0 scale-75 pointer-events-none"
                  : "max-w-[30px] opacity-100 scale-100"
              }`}
            >
              <button
                onClick={onSearchOpen}
                className="p-0.5 hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded cursor-pointer"
              >
                <SearchIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
              </button>
            </div>
            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                isSearchOpen
                  ? "max-w-[200px] opacity-100"
                  : "max-w-0 opacity-0 pointer-events-none"
              }`}
            >
              <div className="flex items-center gap-1 px-1.5 py-2 h-[26px] border border-border-gray dark:border-border-gray-dk rounded-md bg-popper-bg dark:bg-popper-bg-dk">
                <SearchIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
                <input
                  ref={searchInputRef as React.RefObject<HTMLInputElement>}
                  type="text"
                  value={searchValue}
                  onChange={handleSearchInputChange}
                  placeholder="Search comments"
                  className="py-2 text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk bg-transparent border-none outline-none w-[120px]"
                />
                <button
                  onClick={onSearchClose}
                  className="p-[2.5px] bg-verba-indigo dark:bg-verba-indigo-dk rounded-full cursor-pointer shrink-0"
                >
                  <CloseIcon className="w-1.5 h-1.5 text-pure-white dark:text-pure-white-dk" />
                </button>
              </div>
            </div>
            <div className="relative mt-[3px]">
              <button
                data-sort-button
                onClick={onSortToggle}
                className="p-0.5 hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded cursor-pointer"
              >
                <SortAscendingIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
              </button>
              {enableSort === "activity" && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onMouseDown={onSortClose}
                  />
                  <ActivitySortDropdown
                    sortOrder={sortOrder}
                    onSort={onSort}
                    onReset={onSortReset}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityMainHeader;
